/* ff-lane.js (site overhaul, Jan 2026-10-23): the few things the lane pages do by hand.
   - The gallery: a thumbnail paints its picture into the main frame. With the D&D page's cards (ff-lane-main's
     Collector cards), the last thumbnail is the visitor's card and shows it in the main frame (Jan, 2026-10-24);
     ff-collector.js draws both copies.
   - The size rows: they carry the live waitlist's contract (#dds-sizes .dds-row, dds:state), so ff-turn-queue
     mounts its box, relabels the rows and runs the turn checkout exactly as on the D&D page. The rows here only
     pick a size and say what it costs, and hand their painting and size to a certificate set to follow them.
   - window.ffHeroImage is a no-op: on a turn the waitlist would paint the hidden checkout product's D&D pictures
     into the hero slot, and these pages keep their own art instead.
   - The collector card: a tap turns it over; the colour and frame buttons repaint it.
   - The projects brief: four steps, the project rows first; with a phone the brief also joins Jan's project pipeline
     before it is sent (sending is the consent). The projects gallery drifts across a long picture so it shows whole.
   - A steps section set to the animated path draws its path when it comes on screen.
   Loaded by every ff-lane section; it runs once and does nothing on a page without them. */
(function () {
  if (window.__fflLane) return;
  window.__fflLane = true;

  function all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function one(sel, root) { return (root || document).querySelector(sel); }
  function press(list, on) {
    list.forEach(function (el) {
      var active = el === on;
      el.classList.toggle('ffl-on', active);
      el.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function gallery(g) {
    var main = one('.ffl-gal-main img', g);
    var thumbs = all('[data-ffl-src]', g);
    if (!main || !thumbs.length) return;
    var tile = one('[data-ffl-card-thumb]', g), view = one('[data-ffl-card-view]', g);
    var keys = tile && view ? thumbs.concat([tile]) : thumbs;
    function showCard(on) {
      if (!view) return;
      view.classList.toggle('ffl-show', on);
      view.setAttribute('aria-hidden', on ? 'false' : 'true');
    }
    var timer = null;
    function paint(src, srcset, alt) {
      if (!src) return;
      if (timer) { clearTimeout(timer); timer = null; }
      main.style.opacity = '0';
      timer = setTimeout(function () {
        timer = null;
        if (srcset) main.setAttribute('srcset', srcset); else main.removeAttribute('srcset');
        main.setAttribute('src', src);
        if (alt != null) main.setAttribute('alt', alt);
        var show = function () { main.style.opacity = '1'; };
        if (main.complete) show(); else { main.onload = show; main.onerror = show; setTimeout(show, 500); }
      }, 170);
    }
    thumbs.forEach(function (b) {
      b.addEventListener('click', function () {
        press(keys, b);
        showCard(false);
        paint(b.getAttribute('data-ffl-src'), b.getAttribute('data-ffl-srcset'), b.getAttribute('data-ffl-alt'));
      });
    });
    if (tile && view) tile.addEventListener('click', function () { press(keys, tile); showCard(true); });
  }

  /* The projects gallery's drift-pan for long pictures is gone (Jan, 2026-10-29): that gallery is ff-projects.js now,
     where a mouse leans into a long picture and a tap opens it big. */

  /* A steps section set to the animated path (Jan, 2026-10-24: "make it animation or something not just random lines").
     It waits unlit only when this script runs; otherwise, and with reduced motion, it is lit. */
  function journey(j) {
    /* Jan, 2026-10-29: "the first time you scroll should have a connecting animation with particle effects and
       satisfying clicks as each one connects and plays an animation instead of just showing it". The path waits unlit
       until someone scrolls it into view (or it has sat in view for 3 s). Then a spark runs from medallion to medallion:
       each one clicks as it connects, pops, rings, throws gold sparks, draws its icon and lifts its words. The click is a
       soft made-up tick that rises a step each time, only once the page has had a tap (browsers allow no sound before
       one) and never while the site's sound is muted ('ffd-muted'); Android adds a tiny buzz. With reduced motion it
       simply stands lit. */
    var rm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (rm || !window.IntersectionObserver || !window.requestAnimationFrame || j.__fflJy) return;
    j.__fflJy = true;
    var steps = all('.ffl-jy-st', j);
    if (steps.length < 2) return;
    var nodes = steps.map(function (s) { return one('.ffl-jy-node', s); });
    all('.ffl-jy-node svg path, .ffl-jy-node svg circle, .ffl-jy-node svg rect', j).forEach(function (p) { p.setAttribute('pathLength', '1'); });
    j.classList.add('ffl-jy-wait');
    j.style.setProperty('--p', '0');
    steps.forEach(function (s) { s.style.setProperty('--seg', '0'); });

    var started = false, seen = false, scrolled = false, idle = 0;
    var io = new IntersectionObserver(function (es) {
      seen = es[es.length - 1].isIntersecting;
      clearTimeout(idle);
      if (seen && !scrolled) idle = setTimeout(start, 3000);
      if (seen && scrolled) start();
    }, { rootMargin: '0px 0px -22% 0px', threshold: 0.25 });
    io.observe(j);
    function onScroll() { scrolled = true; if (seen) start(); }
    window.addEventListener('scroll', onScroll, { passive: true });
    function start() {
      if (started) return;
      started = true; clearTimeout(idle); io.disconnect(); window.removeEventListener('scroll', onScroll);
      run();
    }

    function run() {
      var cv = document.createElement('canvas'), ctx = cv.getContext('2d');
      if (!ctx) { finish(); return; }
      var PAD = 80, dpr = Math.min(2, window.devicePixelRatio || 1), C = [], parts = [], head = null, raf = 0, last = 0;
      cv.className = 'ffl-jy-fx';
      cv.setAttribute('aria-hidden', 'true');
      j.appendChild(cv);
      function measure() {
        var b = j.getBoundingClientRect();
        cv.width = Math.ceil((b.width + PAD * 2) * dpr); cv.height = Math.ceil((b.height + PAD * 2) * dpr);
        cv.style.width = (b.width + PAD * 2) + 'px'; cv.style.height = (b.height + PAD * 2) + 'px';
        C = nodes.map(function (n) {
          var r = n.getBoundingClientRect();
          return { x: r.left + r.width / 2 - b.left + PAD, y: r.top + r.height / 2 - b.top + PAD, r: r.width / 2 };
        });
      }
      measure();
      window.addEventListener('resize', measure);

      function spark(x, y, sp, life, size, hue) {
        var a = Math.random() * Math.PI * 2, v = sp * (0.35 + Math.random() * 0.65);
        parts.push({ x: x, y: y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - sp * 0.12, t: 0, life: life * (0.6 + Math.random() * 0.4), s: size * (0.5 + Math.random() * 0.7), h: hue });
      }
      function burst(i) {
        var c = C[i], k;
        for (k = 0; k < 40; k++) { spark(c.x, c.y, 0.48, 950, 3.2, 40 + Math.random() * 12); var p = parts[parts.length - 1]; p.x += p.vx * c.r * 2.2; p.y += p.vy * c.r * 2.2; }
        for (k = 0; k < 14; k++) spark(c.x, c.y, 0.18, 1500, 2.2, 48);
        parts.push({ ring: true, x: c.x, y: c.y, t: 0, life: 620, r0: c.r });
      }
      function loop(now) {
        var dt = last ? Math.min(40, now - last) : 16; last = now;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, cv.width, cv.height);
        ctx.globalCompositeOperation = 'lighter';
        for (var i = parts.length - 1; i >= 0; i--) {
          var p = parts[i];
          p.t += dt;
          if (p.t >= p.life) { parts.splice(i, 1); continue; }
          var f = p.t / p.life;
          if (p.ring) {
            ctx.strokeStyle = 'rgba(255,226,150,' + (0.55 * (1 - f)) + ')';
            ctx.lineWidth = 2 * (1 - f) + 0.5;
            ctx.beginPath(); ctx.arc(p.x, p.y, p.r0 + f * p.r0 * 1.6, 0, Math.PI * 2); ctx.stroke();
            continue;
          }
          p.vx *= Math.pow(0.992, dt); p.vy = p.vy * Math.pow(0.992, dt) + 0.0009 * dt;
          p.x += p.vx * dt; p.y += p.vy * dt;
          var al = f < 0.15 ? f / 0.15 : 1 - (f - 0.15) / 0.85;
          ctx.fillStyle = 'hsla(' + p.h + ',95%,' + (78 - f * 20) + '%,' + al.toFixed(3) + ')';
          ctx.beginPath(); ctx.arc(p.x, p.y, Math.max(0.3, p.s * (1 - f * 0.6)), 0, Math.PI * 2); ctx.fill();
        }
        if (head) {
          var g = ctx.createRadialGradient(head.x, head.y, 0, head.x, head.y, 22);
          g.addColorStop(0, 'rgba(255,248,225,.95)'); g.addColorStop(0.25, 'rgba(255,224,150,.55)'); g.addColorStop(1, 'rgba(242,217,142,0)');
          ctx.fillStyle = g; ctx.beginPath(); ctx.arc(head.x, head.y, 22, 0, Math.PI * 2); ctx.fill();
          for (var e = 0; e < 2; e++) spark(head.x, head.y, 0.05, 420, 1.6, 44 + Math.random() * 8);
        }
        ctx.globalCompositeOperation = 'source-over';
        raf = parts.length || head ? requestAnimationFrame(loop) : 0;
        if (!raf) last = 0;
      }
      function kick() { if (!raf) raf = requestAnimationFrame(loop); }

      function connect(i) {
        steps[i].classList.add('is-lit');
        burst(i); kick();
        tick(i, i === steps.length - 1);
      }
      function travel(i, done) {
        var a = C[i], b = C[i + 1], d = Math.hypot(b.x - a.x, b.y - a.y), ms = Math.max(360, Math.min(620, d * 1.6)), t0 = 0;
        var n1 = Math.max(1, steps.length - 1);
        (function step(now) {
          if (!t0) t0 = now;
          var u = Math.min(1, (now - t0) / ms), e = u < 0.5 ? 2 * u * u : 1 - Math.pow(-2 * u + 2, 2) / 2;
          var A = C[i], B = C[i + 1];
          head = { x: A.x + (B.x - A.x) * e, y: A.y + (B.y - A.y) * e };
          j.style.setProperty('--p', ((i + e) / n1).toFixed(4));
          steps[i].style.setProperty('--seg', e.toFixed(4));
          kick();
          if (u < 1) requestAnimationFrame(step); else { head = null; done(); }
        })(performance.now());
      }
      var i = 0;
      connect(0);
      setTimeout(function next() {
        if (i >= steps.length - 1) { setTimeout(finish, 1100); return; }
        travel(i, function () { i++; connect(i); setTimeout(next, 170); });
      }, 260);
      function finish() {
        j.classList.add('ffl-in', 'ffl-jy-done');
        j.classList.remove('ffl-jy-wait');
        steps.forEach(function (s) { s.classList.remove('is-lit'); s.style.removeProperty('--seg'); });
        j.style.removeProperty('--p');
        window.removeEventListener('resize', measure);
        (function gone() { if (raf) { setTimeout(gone, 200); return; } if (cv.parentNode) cv.parentNode.removeChild(cv); })();
      }
    }
  }

  /* the click as a medallion connects: a short wooden tock with a bright edge, a little higher each step, the last one
     with a fifth above it. Made in the browser, so there is no file to fetch. */
  var jyAC = null;
  function tick(i, end) {
    var ua = navigator.userActivation;
    if (ua && !ua.hasBeenActive) return;
    try { if (localStorage.getItem('ffd-muted') === '1') return; } catch (e) { /* storage blocked: sound stays allowed */ }
    try {
      var AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      jyAC = jyAC || new AC();
      if (jyAC.state === 'suspended') jyAC.resume();
      var x = jyAC, t = x.currentTime + 0.01, f = 620 * Math.pow(2, (i * 3) / 12), out = x.createGain();
      out.gain.value = 0.55;
      out.connect(x.destination);
      var tone = function (type, hz, from, peak, len, at) {
        var o = x.createOscillator(), g = x.createGain();
        o.type = type;
        o.frequency.setValueAtTime(hz * from, at); o.frequency.exponentialRampToValueAtTime(hz, at + 0.025);
        g.gain.setValueAtTime(0.0001, at); g.gain.exponentialRampToValueAtTime(peak, at + 0.004); g.gain.exponentialRampToValueAtTime(0.0001, at + len);
        o.connect(g); g.connect(out); o.start(at); o.stop(at + len + 0.02);
      };
      tone('triangle', f, 1.6, 0.22, 0.14, t);
      tone('sine', f * 3, 1, 0.05, 0.09, t);
      if (end) { tone('sine', f * 1.5, 1, 0.09, 0.42, t + 0.07); tone('sine', f * 2, 1, 0.06, 0.5, t + 0.12); }
      /* the edge: a few milliseconds of bright noise */
      var n = Math.floor(x.sampleRate * 0.006), buf = x.createBuffer(1, n, x.sampleRate), d = buf.getChannelData(0);
      for (var k = 0; k < n; k++) d[k] = (Math.random() * 2 - 1) * (1 - k / n);
      var src = x.createBufferSource(), hp = x.createBiquadFilter(), ng = x.createGain();
      hp.type = 'highpass'; hp.frequency.value = 2600; ng.gain.value = 0.16;
      src.buffer = buf; src.connect(hp); hp.connect(ng); ng.connect(out); src.start(t);
      if (navigator.vibrate) navigator.vibrate(end ? [10, 40, 14] : 8);
    } catch (e) { /* no sound: the light and sparks still play */ }
  }

  /* a certificate set to follow the size shows the chosen row's painting and size (Jan, 2026-10-24: "the cert needs to
     have an image on it as well", the D&D page's certificate follows the size the same way) */
  function follow(r) {
    var pic = r.getAttribute('data-ffl-pic');
    if (!pic) return;
    all('[data-ffl-cert-follow]').forEach(function (c) {
      var img = one('.ffl-cert-art img', c);
      if (img && img.getAttribute('src') !== pic) {
        img.removeAttribute('srcset');
        img.setAttribute('src', pic);
      }
      var dd = one('[data-ffl-cert-size]', c);
      var size = r.getAttribute('data-ffl-cert-size');
      if (dd && size) dd.textContent = size;
    });
  }

  function sizes(box) {
    var rows = all('.dds-row', box);
    var line = one('[data-ffl-sizeline]');
    if (!rows.length) return;
    function say(r) { if (line) line.textContent = r.getAttribute('data-line') || ''; }
    rows.forEach(function (r) {
      r.addEventListener('click', function () {
        /* on an open turn ff-turn-queue owns the rows (its own aria-pressed and checkout); this only keeps the line */
        if (!r.classList.contains('ffq-turn-tier')) {
          rows.forEach(function (x) { x.setAttribute('aria-pressed', x === r ? 'true' : 'false'); });
          try {
            document.dispatchEvent(new CustomEvent('dds:state', { detail: { available: false, name: r.getAttribute('data-size'), variantId: null } }));
          } catch (e) {}
        }
        say(r);
        follow(r);
      });
    });
    var start = one('.dds-row[aria-pressed="true"]', box) || rows[0];
    say(start);
  }

  function card(sec) {
    var flip = one('.ffl-flip', sec);
    if (flip) {
      flip.addEventListener('click', function () {
        var on = !flip.classList.contains('ffl-on');
        flip.classList.toggle('ffl-on', on);
        flip.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    var faces = all('.ffl-flip .ffl-hc', sec);
    var colours = all('[data-ffl-colour]', sec);
    var frames = all('[data-ffl-frame]', sec);
    colours.forEach(function (b) {
      b.addEventListener('click', function () {
        press(colours, b);
        var v = b.getAttribute('data-ffl-colour');
        faces.forEach(function (f) {
          f.className = f.className.replace(/\s*ffl-co-[a-z]+/g, '') + ' ffl-co-' + v;
        });
      });
    });
    frames.forEach(function (b) {
      b.addEventListener('click', function () {
        press(frames, b);
        var v = b.getAttribute('data-ffl-frame');
        faces.forEach(function (f) {
          f.className = f.className.replace(/\s*ffl-fr-[a-z]+/g, '') + (v && v !== 'gold' ? ' ffl-fr-' + v : '');
        });
      });
    });
  }

  /* The brief can also join Jan's project pipeline: the Worker's product waitlist (Klaviyo list, the project
     follow-up emails and texts, the Data Hub's open checkout), with the same call and consent version as the old
     project quiz. Only with a phone; sending is the consent (Jan, 2026-10-24: "remove them if they hit submit those
     should be automatically implied"), and the words over the button say so, the texts part once a phone is typed.
     The brief itself always goes on to the store inbox. A mistyped phone is the one thing that stops the send, so it
     can be fixed or cleared. */
  var PIPELINE = 'https://quest-ai.fondlyframed.workers.dev/product-waitlist/join';
  function e164(raw) {
    var p = String(raw || '').replace(/[\s().-]/g, '');
    if (/^00[1-9]/.test(p)) p = '+' + p.slice(2);
    if (/^\d{10}$/.test(p)) p = '+1' + p;
    else if (/^1\d{10}$/.test(p)) p = '+' + p;
    if (/^\+11\d{10}$/.test(p)) p = p.slice(0, 2) + p.slice(3);
    if (!/^\+[1-9]\d{6,14}$/.test(p) || (/^\+1/.test(p) && !/^\+1[2-9]\d{2}[2-9]\d{6}$/.test(p))) return '';
    return p;
  }
  function uuid() { try { return window.crypto.randomUUID(); } catch (e) { return ''; } }

  /* The brief, one step at a time, the Hall's add-a-hero way (Jan, 2026-10-24: "the form should be step by step like
     how we have when we submit a hero in the hall of heroes"): the project, the brief, timing and budget, then where
     Jan replies. Choosing a project moves on by itself; Continue waits until a step is answered; Back keeps every
     answer. A brief the store sent back with an error opens on the last step with its words still in it. */
  function wizard(form) {
    var steps = all('[data-ffl-step]', form), n = steps.length;
    var next = one('[data-ffl-next]', form), back = one('[data-ffl-back]', form), send = one('[data-ffl-send]', form);
    var count = one('[data-ffl-wiz-count]', form), pips = all('.ffl-wiz-rail i', form);
    if (n < 2 || !next || !back || !send) return null;
    var at = one('[data-ffl-errors]', form) ? n : 1, auto = null;
    function val(id) { var el = one('#' + id, form); return el ? el.value.trim() : ''; }
    function ok(k) {
      if (k === 1) return !!one('input[name="contact[Project]"]:checked', form);
      if (k === 2) return val('ffl-pj-what').length >= 10;
      return true;
    }
    function paint(dir) {
      steps.forEach(function (st) {
        var on = +st.getAttribute('data-ffl-step') === at;
        st.classList.toggle('ffl-wiz-on', on);
        st.classList.remove('ffl-wiz-fwd', 'ffl-wiz-bk');
        if (on && dir) { void st.offsetWidth; st.classList.add(dir > 0 ? 'ffl-wiz-fwd' : 'ffl-wiz-bk'); }
      });
      pips.forEach(function (p, i) { p.className = i + 1 < at ? 'ffl-done' : (i + 1 === at ? 'ffl-now' : ''); });
      if (count) count.textContent = 'Step ' + at + ' of ' + n;
      back.hidden = at === 1;
      next.hidden = at === n;
      send.hidden = at !== n;
      next.disabled = !ok(at);
    }
    function go(k, focus) {
      if (k < 1 || k > n || k === at) return;
      var dir = k > at ? 1 : -1;
      at = k; paint(dir);
      /* the card's top stays in view under the sticky header (no smooth scroll on a step change) */
      var r = form.getBoundingClientRect(), hd = document.querySelector('.section-header, sticky-header, header'), top = 12;
      if (hd) { var hr = hd.getBoundingClientRect(); if (hr.bottom > 0 && hr.bottom < window.innerHeight / 2) top += hr.bottom; }
      if (r.top < top) window.scrollBy(0, r.top - top);
      if (focus) {
        var f = one('.ffl-wiz-on input:not([type="hidden"]):not([type="radio"]), .ffl-wiz-on textarea', form) || one('.ffl-wiz-on input:checked', form);
        if (f) { try { f.focus({ preventScroll: true }); } catch (e) {} }
      }
    }
    next.addEventListener('click', function () { if (ok(at)) go(at + 1, true); });
    back.addEventListener('click', function () { clearTimeout(auto); go(at - 1, false); });
    all('input[name="contact[Project]"]', form).forEach(function (r) {
      r.addEventListener('change', function () {
        paint(0);
        clearTimeout(auto);
        if (at === 1) auto = setTimeout(function () { if (at === 1) go(2, true); }, 280);
      });
    });
    /* a chosen row or chip wears the gold ring (a class as well as :checked, for older WebKit) */
    function marks() { all('input[type="radio"]', form).forEach(function (r) { var l = r.closest('label'); if (l) l.classList.toggle('ffl-on', r.checked); }); }
    form.addEventListener('change', marks);
    marks();
    form.addEventListener('input', function () { next.disabled = !ok(at); });
    /* Enter in a field moves on rather than sending a half-filled brief */
    form.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' || at === n || !e.target || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON') return;
      e.preventDefault();
      if (ok(at)) go(at + 1, true);
    });
    form.classList.add('ffl-wiz-js');
    paint(0);
    return { at: function () { return at; } };
  }

  function brief(sec) {
    var form = one('#ffl-brief-form', sec);
    if (!form) return;
    wizard(form);
    var phone = one('[data-ffl-phone]', sec);
    if (!phone) return;
    var smsWords = one('[data-ffl-sms-words]', sec), smsYes = one('[data-ffl-sms-yes]', sec);
    var err = one('[data-ffl-phone-err]', sec);
    var btn = one('button[type="submit"]', form);
    function say(text) {
      if (err) { err.textContent = text || ''; err.hidden = !text; }
      if (text) phone.setAttribute('aria-invalid', 'true'); else phone.removeAttribute('aria-invalid');
    }
    function reveal() {
      var on = !!phone.value.trim();
      if (smsWords) smsWords.hidden = !on;
      if (smsYes) smsYes.disabled = !on;
    }
    phone.addEventListener('input', function () { say(''); reveal(); });
    reveal();

    var passing = false;
    form.addEventListener('submit', function (ev) {
      if (passing) return;
      var raw = phone.value.trim();
      if (!raw) return;
      var p = e164(raw);
      if (!p) {
        ev.preventDefault(); ev.stopImmediatePropagation();
        say('Check the phone number, or leave it empty.');
        phone.focus();
        return;
      }
      phone.value = p;
      var val = function (id) { var el = one('#' + id, form); return el ? el.value.trim() : ''; };
      var pick = function (name) { var el = one('input[name="contact[' + name + ']"]:checked', form); return el ? el.value : ''; };
      var notes = val('ffl-pj-what'), id1 = uuid(), id2 = uuid();
      if (!id1 || !id2 || notes.length < 10 || !window.fetch) return;
      ev.preventDefault(); ev.stopImmediatePropagation();
      var label = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
      var ctrl = window.AbortController ? new AbortController() : null;
      var finished = false;
      var timer = setTimeout(function () { if (ctrl) ctrl.abort(); send(); }, 6000);
      function reset() { clearTimeout(timer); if (btn) { btn.disabled = false; btn.textContent = label; } }
      function send() {
        if (finished) return;
        finished = true; reset(); passing = true;
        if (form.requestSubmit) form.requestSubmit(btn || undefined); else form.submit();
      }
      fetch(PIPELINE, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        signal: ctrl ? ctrl.signal : undefined,
        body: JSON.stringify({
          product: 'project', request_id: id1, receipt: id2, consent_version: 'product-waitlist-2026-10-09',
          first_name: val('ffl-pj-name'), email: val('ffl-pj-email'), phone: p, sms_consent: true, email_consent: true,
          details: { project_type: pick('Project'), project_scope: pick('Budget'),
            project_timeline: pick('When'), project_notes: notes }
        })
      }).then(function (r) {
        return r.json().catch(function () { return {}; });
      }).then(function (j) {
        if (!finished && j && j.error === 'invalid_phone') {
          finished = true; reset();
          say('Check the phone number, or leave it empty.');
          phone.focus();
          return;
        }
        send();
      }, send);
    }, true);
  }

  function init() {
    if (!one('.ffl')) return;
    if (one('#dds-sizes.ffl-sizes') && !window.ffHeroImage) {
      window.ffHeroImage = { snapshot: function () {}, restore: function () {}, show: function () {} };
    }
    all('[data-ffl-gal]').forEach(gallery);
    all('#dds-sizes.ffl-sizes').forEach(sizes);
    all('[data-ffl-card]').forEach(card);
    all('[data-ffl-brief]').forEach(brief);
    all('[data-ffl-journey]').forEach(journey);
  }
  /* ffHeroImage must exist before ff-turn-queue looks for it, so it is set as soon as this file runs */
  if (document.querySelector('#dds-sizes.ffl-sizes')) {
    window.ffHeroImage = window.ffHeroImage || { snapshot: function () {}, restore: function () {}, show: function () {} };
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
